import { useNavigate } from 'react-router-dom';
import { ProductDetail } from '@/mocks/productDetails';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useState } from 'react';

interface DealProductCardProps {
  product: ProductDetail;
  dealDiscount?: number;
}

export default function DealProductCard({ product, dealDiscount }: DealProductCardProps) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { primary, primaryLight: light, hexAlpha } = useThemeColor();
  const [added, setAdded] = useState(false);

  const displayPrice = dealDiscount
    ? Math.round(product.price * (1 - dealDiscount / 100))
    : product.price;

  const originalDisplay = dealDiscount ? product.price : product.originalPrice;
  const savingAmount = originalDisplay - displayPrice;
  const savingPercent = Math.round((savingAmount / originalDisplay) * 100);

  const fav = isFavorite(product.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addItem({
      id: product.id,
      nameAr: product.nameAr,
      price: displayPrice,
      image: product.images?.[0] || '',
      category: product.category,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      className="group relative bg-[#111] border border-white/5 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300"
      onClick={() => navigate(`/product/${product.id}`)}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.25);
        (e.currentTarget as HTMLElement).style.boxShadow = `0 0 40px ${hexAlpha(primary, 0.07)}`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)";
        (e.currentTarget as HTMLElement).style.boxShadow = "none";
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-52 bg-[#0f0f0f]">
        <img
          src={product.images?.[0] || ''}
          alt={product.nameAr}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(to right, transparent, ${hexAlpha(primary, 0.4)}, transparent)` }}></div>

        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          {savingPercent > 0 && (
            <span
              className="bg-red-500 text-white text-xs font-black px-2.5 py-1 rounded-full whitespace-nowrap"
              style={{ fontFamily: "'Tajawal', sans-serif", boxShadow: "0 0 10px rgba(239,68,68,0.4)" }}
            >
              -{savingPercent}%
            </span>
          )}
          {product.badge && (
            <span
              className="text-black text-xs font-black px-2.5 py-1 rounded-full whitespace-nowrap"
              style={{ fontFamily: "'Tajawal', sans-serif", background: `linear-gradient(to right, ${light}, ${primary})` }}
            >
              {product.badge}
            </span>
          )}
        </div>

        {/* Fav button */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
          className={`absolute top-3 left-3 w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer border ${
            fav
              ? 'bg-red-500 border-red-500 text-white'
              : 'bg-black/50 border-white/10 text-gray-300 hover:border-red-400/40 hover:text-red-400'
          }`}
        >
          <i className={fav ? 'ri-heart-fill text-sm' : 'ri-heart-line text-sm'}></i>
        </button>
      </div>

      {/* Info */}
      <div className="p-4">
        <p
          className="text-xs mb-1.5 tracking-wide"
          style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.5) }}
        >
          {product.category}
        </p>
        <h3
          className="text-white font-bold text-sm mb-3 leading-snug transition-colors"
          style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}
        >
          {product.nameAr}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-4">
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className="text-xs ri-star-fill" style={{ color: i < product.rating ? primary : "#374151" }}></i>
            ))}
          </div>
          <span className="text-gray-500 text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>({product.reviews})</span>
        </div>

        {/* Price + Cart */}
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span
                className="font-bold text-xl"
                style={{
                  fontFamily: "'Amiri', 'Tajawal', serif",
                  background: `linear-gradient(to left, ${light}, ${primary})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  filter: `drop-shadow(0 0 8px ${hexAlpha(primary, 0.3)})`,
                }}
              >
                {displayPrice.toLocaleString('ar-SA')}
              </span>
              <span className="text-xs" style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.6) }}>ر.س</span>
            </div>
            {savingAmount > 0 && (
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-gray-600 text-xs line-through" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                  {originalDisplay.toLocaleString('ar-SA')} ر.س
                </span>
                <span className="text-emerald-400 text-xs font-bold" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                  وفّر {savingAmount.toLocaleString('ar-SA')}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={handleAddToCart}
            className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all cursor-pointer shrink-0 ${added ? 'bg-emerald-500 text-white' : 'text-black'}`}
            style={!added ? {
              background: `linear-gradient(135deg, ${light}, ${primary})`,
              boxShadow: `0 0 15px ${hexAlpha(primary, 0.2)}`,
            } : { boxShadow: "0 0 15px rgba(52,211,153,0.3)" }}
          >
            <i className={`text-sm ${added ? 'ri-check-line' : 'ri-shopping-cart-line'}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
}
